import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import TicketForm from '../components/TicketForm';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// Backend now sends datetimes as plain 'YYYY-MM-DDTHH:mm' strings
// These can be used directly in datetime-local inputs — no conversion needed
const fmt = dt => dt ? dt.toString().slice(0, 16) : '';

export default function EditTicket() {
  const { ticket_id } = useParams();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  useEffect(() => {
    setLoading(true);
    api.get(`/tickets/${ticket_id}`)
      .then(({ data: d }) => {
        const t = d.ticket;
        const canEdit = isAdmin || (
          user?.role === 'user' &&
          t.ats_consultant?.trim().toLowerCase() === user?.full_name?.trim().toLowerCase()
        );
        if (!canEdit) { navigate('/tickets'); return; }
        setData(d);
      })
      .catch(() => navigate('/tickets'))
      .finally(() => setLoading(false));
  }, [ticket_id]);

  const handleSubmit = async (form, calls) => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put(`/tickets/${ticket_id}`, form);
      const existing = data.calls || [];
      for (const c of existing) { await api.delete(`/calls/${c.id}`); }
      for (const c of calls) {
        if (c.rd_start_dt || c.call_start_dt || c.call_notes) {
          await api.post('/calls', { ...c, ticket_id });
        }
      }
      setSuccess('Ticket updated successfully!');
      setTimeout(() => navigate(`/tickets/${ticket_id}`), 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Update failed');
    } finally { setSaving(false); }
  };

  if (loading || !data) return (
    <Layout title="Edit Ticket">
      <div style={{textAlign:'center',padding:60,color:'#aaa'}}>
        <i className="fa-solid fa-spinner fa-spin" style={{fontSize:28}}></i>
      </div>
    </Layout>
  );

  const t = data.ticket;
  const initial = {
    opened_on:          fmt(t.opened_on),
    opened_by_name:     t.opened_by_name  || '',
    opened_by_email:    t.opened_by_email || '',
    support_plan_id:    t.support_plan_id    || '',
    product_type_id:    t.product_type_id    || '',
    platform_id:        t.platform_id        || '',
    ats_consultant_id:  t.ats_consultant_id  || '',
    sales_manager_id:   t.sales_manager_id   || '',
    client_id:          t.client_id          || '',
    priority:           t.priority,
    status:             t.status,
    issue_short_desc:   t.issue_short_desc   || '',
    issue_detail_desc:  t.issue_detail_desc  || '',
    classification_id:  t.classification_id  || '',
    category_id:        t.category_id        || '',
    operating_system:   t.operating_system   || '',
    product_version:    t.product_version    || '',
    gtl_responded_on:   fmt(t.gtl_responded_on),
    latest_update_note: t.latest_update_note || '',
    resolution_desc:    t.resolution_desc    || '',
    remarks:            t.remarks            || ''
  };

  const initCalls = (data.calls || []).map(c => ({
    id:            c.id,
    rd_start_dt:   fmt(c.rd_start_dt),
    rd_end_dt:     fmt(c.rd_end_dt),
    call_start_dt: fmt(c.call_start_dt),
    call_end_dt:   fmt(c.call_end_dt),
    call_notes:    c.call_notes || ''
  }));

  return (
    <Layout title={`Edit Ticket — ${ticket_id}`}>
      <TicketForm
        initial={initial}
        calls={initCalls}
        onSubmit={handleSubmit}
        loading={saving}
        error={error}
        success={success}
        isEdit={true}
      />
    </Layout>
  );
}